import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { FormlyFieldConfig } from '@ngx-formly/core';

@Injectable({
  providedIn: 'root',
})
export class VerticalStepperService {
  private selectedIndexSubject$ = new BehaviorSubject<number>(0);
  selectedIndexObservable$ = this.selectedIndexSubject$.asObservable();
  private readonly draftSavingModel$ = new BehaviorSubject<
    Record<string, unknown>
  >({});
  draftSavingModelObservable$ = this.draftSavingModel$.asObservable();
  private draftIsSaving$ = new BehaviorSubject<boolean>(false);
  draftIsSavingObservable$ = this.draftIsSaving$.asObservable();

  private readonly childNextClickedSubject$ = new Subject<{
    currentIndex: number;
    isLastStep: boolean;
    field: FormlyFieldConfig;
  }>();
  childNextClickedObservable$ = this.childNextClickedSubject$.asObservable();

  private readonly behaviorRegistry = new Map<
    string,
    (
      stepperIndex: number,
      isLastStep: boolean,
      field: FormlyFieldConfig
    ) => boolean
  >();

  private readonly nestedContextRegistry = new Set<string>();

  // Subject to request validation from nested steppers
  private readonly validateNestedStepperSubject$ = new Subject<{
    stepIndex: number;
    callback: (isValid: boolean) => void;
  }>();
  validateNestedStepperObservable$ =
    this.validateNestedStepperSubject$.asObservable();

  setSelectedIndex(index: number) {
    this.selectedIndexSubject$.next(index);
  }

  triggerDraftSaving(model: Record<string, unknown>) {
    this.draftSavingModel$.next(model);
  }

  triggerDraftSavingBoolean(toggle: boolean) {
    this.draftIsSaving$.next(toggle);
  }

  resetStepper() {
    this.selectedIndexSubject$.next(0);
  }

  registerBehaviorOverride(
    id: string,
    handler: (
      stepperIndex: number,
      isLastStep: boolean,
      field: FormlyFieldConfig
    ) => boolean
  ): string {
    this.behaviorRegistry.set(id, handler);
    return id;
  }

  unregisterBehaviorOverride(id: string): void {
    this.behaviorRegistry.delete(id);
  }

  handleLastStepBehavior(
    stepperIndex: number,
    isLastStep: boolean,
    field: FormlyFieldConfig
  ): boolean {
    for (const [, handler] of this.behaviorRegistry.entries()) {
      if (handler(stepperIndex, isLastStep, field)) {
        return true;
      }
    }
    return false;
  }

  triggerChildNextClick(
    currentIndex: number,
    isLastStep: boolean,
    field: FormlyFieldConfig
  ): void {
    this.childNextClickedSubject$.next({
      currentIndex,
      isLastStep,
      field,
    });
  }

  registerNestedContext(contextId: string): void {
    this.nestedContextRegistry.add(contextId);
  }

  unregisterNestedContext(contextId: string): void {
    this.nestedContextRegistry.delete(contextId);
  }

  isInNestedContext(): boolean {
    return this.nestedContextRegistry.size > 0;
  }

  /**
   * Request validation from nested steppers in the current step
   * Used by parent steppers to validate child steppers before proceeding
   */
  requestNestedStepperValidation(
    stepIndex: number,
    callback: (isValid: boolean) => void
  ): void {
    this.validateNestedStepperSubject$.next({ stepIndex, callback });
  }
}
